import { useEffect, useMemo, useState } from 'preact/hooks'
import { CarouselResults } from './CarouselResults'
import { SearchspringCustomProductsProvider } from '../fetchers/customProducts'
import { Cart } from '@/components/helpers/cart'
import { session } from '@/components/helpers/session'
import Icon from '@/components/snippets/icon/icon'
import { SearchspringCollectionProvider } from '../fetchers/collection'

export function CarouselPwp({ products, itemClass }) {
  const pwpSettings = GM_STATE.metaobjects.product_with_purchase
  const [cartItems, setCartItems] = useState(window.Cart?.value?.items || [])

  // Get product IDs from cart items
  const currentProductIds = useMemo(() => cartItems?.map((item) => item.product_id) || [], [cartItems])

  // Filter settings that have matching trigger products
  const matchingSettings = useMemo(
    () =>
      pwpSettings?.filter((setting) => {
        // Check product triggers if they exist
        if (setting.upsellTriggerProducts?.length) {
          return setting.upsellTriggerProducts.some((triggerProduct) => currentProductIds.includes(triggerProduct.id))
        }

        // If no product triggers, check collection triggers
        if (setting.upsellTriggerCollection?.length) {
          return cartItems.some((item) =>
            item.collections?.some((itemCollection) =>
              setting.upsellTriggerCollection.some((triggerCollection) => triggerCollection.id === itemCollection.id)
            )
          )
        }

        return false
      }) || [],
    [pwpSettings, currentProductIds, cartItems]
  )

  // Get the setting with lowest priority
  const activeSetting = useMemo(
    () =>
      matchingSettings.length > 0
        ? matchingSettings.reduce((prev, current) => (prev.priority < current.priority ? prev : current))
        : null,
    [matchingSettings]
  )

  // Show modal
  useEffect(() => {
    const modal = document.querySelector('[data-id="custom-modal"]')
    const mutedPwp = session.pwpMute.get()
    if (activeSetting && modal && mutedPwp !== activeSetting.title) {
      modal.setAttribute('data-ui', 'visible')
      if (activeSetting.exclusionDays) {
        session.pwpMute.set(activeSetting.title, parseInt(activeSetting.exclusionDays))
      }
    }
  }, [activeSetting])

  // React to cart updates
  useEffect(() => {
    const handleCartUpdate = (e) => {
      if (e.detail.isInitial) return // this makes it so the popup does not show on page load
      setCartItems(window.Cart?.value?.items || [])
    }
    document.addEventListener(Cart.EVENT_UPDATE_CART_COUNT, handleCartUpdate)
    return () => {
      document.removeEventListener(Cart.EVENT_UPDATE_CART_COUNT, handleCartUpdate)
    }
  }, [])

  if (!activeSetting) return null

  return (
    <PwpModal>
      <div className="flex flex-col gap-6 md:gap-8">
        <div className="relative z-10 flex w-fit flex-col gap-2">
          <div className="h4 md:h2 pr-6 text-primary-blue md:pr-0">{activeSetting.title}</div>
          <div className="text-lg text-primary-black">{activeSetting.description}</div>
        </div>

        <div style="--translate-y-18:-4.5rem;" className="md:mb-[-4.5rem] max-w-full md:translate-y-[--translate-y-18]">
          {activeSetting.upsellProducts?.length ? (
            <SearchspringCustomProductsProvider
              profile="pdp-recently-viewed"
              skus={activeSetting.upsellProducts.map((product) => product.variants[0].sku)}
              limit={activeSetting.productsToShow || undefined}
            >
              <CarouselResults className={itemClass} hideControlsOnMobile={true} />
            </SearchspringCustomProductsProvider>
          ) : activeSetting.upsellCollection?.handle ? (
            <SearchspringCollectionProvider
              handle={activeSetting.upsellCollection.handle}
              resultsPerPage={activeSetting.productsToShow || undefined}
            >
              <CarouselResults className={itemClass} hideControlsOnMobile={true} />
            </SearchspringCollectionProvider>
          ) : null}
        </div>

        {!!activeSetting.upsellCollection?.handle && (
          <a
            href={`/collections/${activeSetting.upsellCollection.handle}`}
            className="btn mt-4 text-base md:text-lg font-bold underline underline-offset-4 ring-offset-2"
          >
            View all
          </a>
        )}
      </div>
    </PwpModal>
  )
}

function PwpModal({ children }) {
  return (
    <x-modal data-id="custom-modal" className="group/custom-modal">
      <div
        data-modal-view="1"
        className="pointer-events-none fixed inset-0 z-[60] m-4 !mx-auto !my-auto flex h-fit w-11/12 max-w-screen-md translate-x-96 flex-col overflow-y-auto rounded-xl bg-primary-white opacity-0 transition-all duration-500 group-data-visible/custom-modal:pointer-events-auto group-data-visible/custom-modal:translate-x-0 group-data-visible/custom-modal:opacity-100 md:m-6"
      >
        <div class="absolute right-0 top-0 z-20 flex items-center justify-end p-2">
          <div data-modal-parent="custom-modal" data-modal-role="close">
            <Icon id="close" className="min-h-6 min-w-6 cursor-pointer" />
          </div>
        </div>
        <div className="overflow-hidden p-4 md:p-6 md:pt-10">{children}</div>
      </div>
      <div
        data-modal-parent="custom-modal"
        data-modal-role="close"
        className="pointer-events-none fixed inset-0 z-[59] block cursor-pointer bg-black opacity-0 duration-150 group-data-visible/custom-modal:pointer-events-auto group-data-visible/custom-modal:opacity-60"
      ></div>
    </x-modal>
  )
}
